import { batch, useDispatch } from 'react-redux';
import { executeApiAction } from '../../../../utility/apiHelper';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import {
  getUserGridLayout,
  saveUserGridLayout,
  resetUserGridLayout,
} from '../../../../services/userGridLayoutService';

const useServerGridLayout = ({ enabled = false, gridKey, gridState, defaultPageSize = 25 }) => {
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(!enabled);
  const [saving, setSaving] = useState(false);
  const [savedLayout, setSavedLayout] = useState(null);

  const gridStateRef = useRef(gridState);
  gridStateRef.current = gridState;
  const captureBaselineRef = useRef(true);

  const currentLayout = useMemo(
    () => ({
      ColumnOrder: gridState.columnOrder,
      ColumnVisibility: gridState.columnVisibilityModel,
      ColumnWidths: gridState.columnWidths,
      PinnedColumns: gridState.pinnedColumns,
      SortModel: gridState.sortModel,
      Density: gridState.density,
      PageSize: gridState.pageSize,
    }),
    [gridState]
  );

  const isDirty = loaded && savedLayout !== null && !isEqual(currentLayout, savedLayout);

  const safeArray = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const safeObject = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (loaded && !saving && captureBaselineRef.current) {
      captureBaselineRef.current = false;
      setSavedLayout(currentLayout);
    }
  }, [loaded, saving, currentLayout]);

  useEffect(() => {
    if (!enabled || !gridKey) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
    setSavedLayout(null);
    captureBaselineRef.current = true;

    let cancelled = false;

    executeApiAction({
      dispatch,
      action: () => getUserGridLayout(gridKey),
      onSuccess: (layout) => {
        if (!cancelled && layout) {
          const state = gridStateRef.current;
          const columnOrder = safeArray(layout.ColumnOrder);
          const sortModel = safeArray(layout.SortModel);
          const columnVisibility = safeObject(layout.ColumnVisibility);
          const columnWidths = safeObject(layout.ColumnWidths);
          const pinnedColumns = safeObject(layout.PinnedColumns);

          if (columnOrder?.length) state.setColumnOrder(columnOrder);
          if (columnVisibility) state.setColumnVisibilityModel(columnVisibility);
          if (columnWidths) state.setColumnWidths(columnWidths);
          if (pinnedColumns?.left?.length || pinnedColumns?.right?.length) {
            state.setPinnedColumns(pinnedColumns);
          }
          if (sortModel) state.setSortModel(sortModel);
          if (layout.Density) state.setDensity(layout.Density);
          if (typeof layout.PageSize === 'number') state.setPageSize(layout.PageSize);
        }
      },
      onFinally: () => {
        if (!cancelled) setLoaded(true);
      },
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, gridKey, dispatch]);

  const saveLayout = useCallback(async () => {
    if (!enabled || !gridKey || !isDirty || saving) return;
    await executeApiAction({
      dispatch,
      setLoading: setSaving,
      action: () => saveUserGridLayout(gridKey, currentLayout),
      onSuccess: () => setSavedLayout(currentLayout),
    });
  }, [enabled, gridKey, currentLayout, isDirty, saving, dispatch]);

  const resetLayout = useCallback(async () => {
    if (!enabled || !gridKey || saving) return;
    await executeApiAction({
      dispatch,
      setLoading: setSaving,
      action: () => resetUserGridLayout(gridKey),
      onSuccess: () => {
        batch(() => {
          captureBaselineRef.current = true;
          gridState.setColumnOrder([]);
          gridState.setColumnVisibilityModel({});
          gridState.setColumnWidths({});
          gridState.setPinnedColumns({ left: [], right: [] });
          gridState.setSortModel([]);
          gridState.setDensity('compact');
          gridState.setPageSize(defaultPageSize);
        });
      },
    });
  }, [defaultPageSize, enabled, gridKey, gridState, saving, dispatch]);

  return { loaded, saving, isDirty, saveLayout, resetLayout };
};

export default useServerGridLayout;
